import React from 'react';
import type { PriorAuthReviewSummary } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { SearchHighlightText } from '../SearchHighlightText/SearchHighlightText';
import { PreviewDateWithStatus } from '../Table';

interface PriorAuthReviewPreviewItemProps {
  review: PriorAuthReviewSummary;
  searchTerm: string;
}

/**
 * Preview item component for prior auth review search results.
 * Highlights fields that match the search term (drug, patient name, DOB).
 */
export const PriorAuthReviewPreviewItem: React.FC<PriorAuthReviewPreviewItemProps> = ({ review, searchTerm }) => {
  const formattedDob = formatStandardDate(review.patient_dob);
  const formattedPrescribedDate = formatStandardDate(review.prescription_recieved_at);
  const isReviewed = review.status === 'submitted';

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Typography
          styledAs="bodySmallDMSans"
          renderedAs="div"
          weight={600}
          color="var(--grayscale-900)"
          className="w-fit"
        >
          <SearchHighlightText searchTerm={searchTerm} value={review.drug_name} fieldType="text" truncate />
        </Typography>

        <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--grayscale-900)">
          Prescribed {formattedPrescribedDate}
        </Typography>

        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            color="var(--grayscale-900)"
            className="max-w-[200px] shrink-0"
          >
            <SearchHighlightText searchTerm={searchTerm} value={review.patient_name} fieldType="name" truncate />
          </Typography>
          {review.patient_dob && (
            <Typography styledAs="bodySmallDMSans" renderedAs="span" color="var(--grayscale-600)">
              (DOB: <SearchHighlightText searchTerm={searchTerm} value={formattedDob} fieldType="date" />)
            </Typography>
          )}
        </div>
      </div>

      <PreviewDateWithStatus date={review.created_at} statusLabel={isReviewed ? 'Reviewed' : undefined} />
    </div>
  );
};

export default PriorAuthReviewPreviewItem;
